import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { loadConfig, getAuthToken } from '../services/api';

const AdvanceSummaryReport = ({ currentUser, stores }) => {
  const isAdminUser = ['super admin', 'application admin', 'admin', 'system administrator'].includes((currentUser?.role || '').toLowerCase());
  const [selectedStore, setSelectedStore] = useState(isAdminUser ? 'ALL' : (currentUser?.store || 'ALL'));
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [summaryStats, setSummaryStats] = useState(null);

  // Fetch advance summary report data
  const fetchAdvanceSummaryReport = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const baseUrl = await loadConfig();
      const token = getAuthToken();
      
      const response = await fetch(`${baseUrl}/Reports/advance-summary?groupBy=store`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/plain, application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch report: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        const transformedData = data.data.map((item) => ({
          ...item,
          id: item.groupKey,
        }));
        
        setReportData(transformedData);
        filterDataByStore(transformedData, selectedStore);
      } else {
        setReportData([]);
        setFilteredData([]);
        setSummaryStats(null);
      }
    } catch (err) {
      console.error('Error fetching advance summary report:', err);
      setError(err.message || 'Failed to load report');
      setReportData([]);
      setFilteredData([]);
      setSummaryStats(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate summary statistics
  const calculateSummaryStats = (data) => {
    if (data.length === 0) {
      setSummaryStats(null);
      return;
    }

    const stats = {
      totalAdvances: data.reduce((sum, item) => sum + (item.totalAdvances || 0), 0),
      totalAmount: data.reduce((sum, item) => sum + (item.totalAmount || 0), 0),
      totalGoldGrams: data.reduce((sum, item) => sum + (item.totalGoldGrams || 0), 0),
      averageAmount: 0,
    };

    stats.averageAmount = stats.totalAdvances > 0 ? stats.totalAmount / stats.totalAdvances : 0;
    setSummaryStats(stats);
  };

  // Filter data by selected store
  const filterDataByStore = (data, storeCode) => {
    if (storeCode === 'ALL') {
      setFilteredData(data);
      calculateSummaryStats(data);
    } else {
      const filtered = data.filter(item => item.groupKey === storeCode);
      setFilteredData(filtered);
      calculateSummaryStats(filtered);
    }
  };

  // Handle store selection change
  const handleStoreChange = (e) => {
    const newStore = e.target.value;
    setSelectedStore(newStore);
    filterDataByStore(reportData, newStore);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchAdvanceSummaryReport();
  }, []);

  // Currency formatter
  const currencyFormatter = (value) => {
    return `₹${Number(value || 0).toLocaleString('en-IN')}`;
  };

  // Column definitions for ag-grid
  const columnDefs = [
    {
      field: 'groupName',
      headerName: 'Store Name',
      flex: 1,
      filter: 'agTextColumnFilter',
      cellClass: 'text-sm font-semibold text-gray-900',
      pinned: 'left',
    },
    {
      field: 'totalAdvances',
      headerName: 'Total Advances',
      width: 200,
      filter: 'agNumberColumnFilter',
      cellClass: 'text-sm text-right font-medium text-blue-600',
      type: 'rightAligned',
      valueFormatter: (params) => Number(params.value || 0).toLocaleString('en-IN'),
    },
    {
      field: 'totalAmount',
      headerName: 'Total Amount',
      width: 200,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => currencyFormatter(params.value),
      cellClass: 'text-sm text-right font-bold text-red-600 bg-red-50',
      type: 'rightAligned',
    },
    {
      field: 'totalGoldGrams',
      headerName: 'Total Gold (gms)',
      width: 200,
      filter: 'agNumberColumnFilter',
      cellClass: 'text-sm text-right font-medium text-amber-600',
      type: 'rightAligned',
      valueFormatter: (params) => {
        const value = Number(params.value || 0);
        return value.toFixed(4);
      },
    },
    {
      field: 'averageAmount',
      headerName: 'Average/Order',
      width: 200,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => currencyFormatter(params.value),
      cellClass: 'text-sm text-right font-medium text-green-600',
      type: 'rightAligned',
    },
  ];

  // Grid options
  const gridOptions = {
    animateRows: true,
    suppressMovableColumns: false,
    rowHeight: 40,
    headerHeight: 45,
    pagination: true,
    paginationPageSize: 20,
    paginationPageSizeSelector: [10, 20, 50, 100],
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 80,
    },
    domLayout: 'autoHeight',
  };

  return (
    <section>
      <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">Advance Summary Report</h2>
            <p className="text-sm text-gray-500 mt-1">Store-wise summary of advance collections and gold transactions</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="flex-1 sm:flex-none">
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                Store Selection
              </label>
              <select 
                value={selectedStore}
                onChange={handleStoreChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              >
                <option value="ALL">All Stores</option>
                {stores.map(store => (
                  <option key={store.code} value={store.code}>
                    {store.name} ({store.code})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <button
                onClick={fetchAdvanceSummaryReport}
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                <i className={`fas fa-sync-alt ${isLoading ? 'animate-spin' : ''}`}></i>
                <span>{isLoading ? 'Loading...' : 'Refresh'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 font-semibold">
              <i className="fas fa-exclamation-circle mr-2"></i>
              {error}
            </p>
          </div>
        )}

        {/* Summary Statistics */}
        {filteredData.length > 0 && summaryStats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700 font-semibold uppercase">Total Advances</p>
              <p className="text-lg font-bold text-blue-900">
                {summaryStats.totalAdvances.toLocaleString('en-IN')}
              </p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 p-3 rounded-lg border border-red-200">
              <p className="text-xs text-red-700 font-semibold uppercase">Total Amount</p>
              <p className="text-lg font-bold text-red-900">
                ₹{summaryStats.totalAmount.toLocaleString('en-IN')}
              </p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-3 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-700 font-semibold uppercase">Total Gold</p>
              <p className="text-lg font-bold text-amber-900">
                {summaryStats.totalGoldGrams.toFixed(2)} gms
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-3 rounded-lg border border-green-200">
              <p className="text-xs text-green-700 font-semibold uppercase">Average/Order</p>
              <p className="text-lg font-bold text-green-900">
                ₹{Math.round(summaryStats.averageAmount).toLocaleString('en-IN')}
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-3 rounded-lg border border-purple-200">
              <p className="text-xs text-purple-700 font-semibold uppercase">Stores Count</p>
              <p className="text-lg font-bold text-purple-900">
                {filteredData.length}
              </p>
            </div>
          </div>
        )}

        {/* AG-Grid Section */}
        <div className="ag-theme-alpine rounded-lg overflow-hidden border border-gray-200" style={{ height: 'auto' }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <i className="fas fa-spinner fa-spin text-4xl text-gray-400 mb-4"></i>
                <p className="text-gray-500 font-semibold">Loading advance summary report...</p>
              </div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <i className="fas fa-inbox text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500 font-semibold">No advance records found</p>
                <p className="text-sm text-gray-400 mt-1">Try adjusting your store selection or refresh the report</p>
              </div>
            </div>
          ) : (
            <AgGridReact
              columnDefs={columnDefs}
              rowData={filteredData}
              gridOptions={gridOptions}
              theme="legacy"
              onGridReady={(params) => setGridApi(params.api)}
              className="w-full"
            />
          )}
        </div>

        {/* Footer Info */}
        {filteredData.length > 0 && (
          <div className="mt-4 text-right text-xs text-gray-500">
            Showing {filteredData.length} store record{filteredData.length !== 1 ? 's' : ''} 
            {selectedStore !== 'ALL' && ` for store: ${selectedStore}`}
          </div>
        )}
      </div>
    </section>
  );
};

export default AdvanceSummaryReport;
